import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, BookOpen, ArrowRight } from 'lucide-react';
interface PreviewMessage {
  id: number;
  text: string;
  sender: 'user' | 'bot';
  articleLink?: {
    title: string;
    url: string;
  };
}
const demoConversation: PreviewMessage[] = [{
  id: 1,
  text: 'How do I stay focused when I feel like giving up?',
  sender: 'user'
}, {
  id: 2,
  text: "Procrastination is usually an emotional regulation problem, not a time management one. Try 'The One Question' reframe: 'What is the very next physical action I need to take?' Make it so small it feels ridiculous to say no to.",
  sender: 'bot',
  articleLink: {
    title: 'One Question to Help You Get More Done',
    url: 'https://theproductivityblueprint.beehiiv.com/p/one-question-to-help-you-get-more-done-d0ae'
  }
}, {
  id: 3,
  text: 'What about when my mind keeps wandering?',
  sender: 'user'
}, {
  id: 4,
  text: "Your mind will wander — that's completely normal. The key is having a system to bring it back. Try the 'one breath reset': take a single deep breath and re-read your last sentence. We covered 15 practical techniques in a recent deep dive.",
  sender: 'bot',
  articleLink: {
    title: 'How to Refocus a Wandering Mind (15 Practical Solutions)',
    url: 'https://theproductivityblueprint.beehiiv.com/p/how-to-refocus-a-wandering-mind-15-practical-solutions'
  }
}];
interface ChatBotPreviewProps {
  onOpenChat: () => void;
}
export function ChatBotPreview({
  onOpenChat
}: ChatBotPreviewProps) {
  const [visibleMessages, setVisibleMessages] = useState<number[]>([]);
  const [hasAnimated, setHasAnimated] = useState(false);
  useEffect(() => {
    if (hasAnimated) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && !hasAnimated) {
        setHasAnimated(true);
        // Stagger message appearance
        demoConversation.forEach((msg, index) => {
          setTimeout(() => {
            setVisibleMessages(prev => [...prev, msg.id]);
          }, index * 800 + 400);
        });
      }
    }, {
      threshold: 0.3
    });
    const el = document.getElementById('chatbot-preview-section');
    if (el) observer.observe(el);
    return () => observer.disconnect();
  }, [hasAnimated]);
  return <section id="chatbot-preview-section" className="py-20 bg-white border-t border-brand-navy/5" data-id="element-49">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-50">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-51">
          {/* Text Column */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-52">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-brand-navy/5 text-brand-navy text-xs font-semibold tracking-wider uppercase rounded-full mb-6" data-id="element-53">
              <MessageSquare size={12} data-id="element-54" />
              AI Productivity Guide
            </div>

            <h2 className="text-3xl md:text-4xl font-serif font-bold text-brand-navy mb-6 leading-tight" data-id="element-55">
              Get Instant Answers to Your Productivity Questions
            </h2>

            <p className="text-lg text-brand-slate mb-8 leading-relaxed" data-id="element-56">
              Our AI mentor is trained on every issue of The Productivity
              Blueprint. Ask it about focus strategies, morning routines, habit
              formation, or overcoming procrastination — and get actionable
              advice with links to the full articles.
            </p>

            <div className="space-y-4 mb-8" data-id="element-57">
              {['Personalized productivity advice on demand', 'Links to relevant newsletter deep dives', 'Covers focus, habits, time management & more'].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-58">
                  <div className="w-5 h-5 rounded-full bg-brand-gold/20 flex items-center justify-center mt-0.5 shrink-0" data-id="element-59">
                    <div className="w-2 h-2 rounded-full bg-brand-gold" data-id="element-60" />
                  </div>
                  <span className="text-brand-slate" data-id="element-61">{item}</span>
                </div>)}
            </div>

            <button onClick={onOpenChat} className="inline-flex items-center gap-3 px-6 py-3 bg-brand-navy text-white rounded-lg font-medium hover:bg-brand-navy/90 transition-colors group" data-id="element-62">
              Try the Productivity Guide
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" data-id="element-63" />
            </button>
          </motion.div>

          {/* Preview Chat Column */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="relative" data-id="element-64">
            {/* Decorative glow */}
            <div className="absolute -top-8 -right-8 w-40 h-40 bg-brand-gold/10 rounded-full blur-3xl pointer-events-none" data-id="element-65" />
            <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-brand-navy/10 rounded-full blur-3xl pointer-events-none" data-id="element-66" />

            {/* Chat Window Mockup */}
            <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden max-w-[400px] mx-auto" data-id="element-67">
              {/* Header */}
              <div className="bg-brand-navy p-4 flex items-center gap-3" data-id="element-68">
                <div className="relative" data-id="element-69">
                  <div className="w-9 h-9 bg-brand-gold rounded-full flex items-center justify-center text-white text-sm" data-id="element-70">
                    ⚡
                  </div>
                  <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-400 rounded-full border-2 border-brand-navy" data-id="element-71" />
                </div>
                <div data-id="element-72">
                  <h4 className="font-bold text-white text-sm" data-id="element-73">
                    Productivity Guide
                  </h4>
                  <p className="text-brand-gold text-xs" data-id="element-74">AI Mentor • Online</p>
                </div>
              </div>

              {/* Messages */}
              <div className="p-4 space-y-3 bg-gray-50 min-h-[360px]" data-id="element-75">
                {/* Welcome message - always visible */}
                <div className="flex flex-col items-start" data-id="element-76">
                  <div className="max-w-[85%] px-3.5 py-2.5 rounded-2xl rounded-bl-none bg-white text-brand-slate border border-gray-100 text-xs leading-relaxed shadow-sm" data-id="element-77">
                    Hi! I'm your AI productivity mentor. What challenge can I
                    help you with today?
                  </div>
                </div>

                <AnimatePresence data-id="element-78">
                  {demoConversation.map(msg => visibleMessages.includes(msg.id) ? <motion.div key={msg.id} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.3
                }} className={`flex flex-col ${msg.sender === 'user' ? 'items-end' : 'items-start'}`} data-id="element-79">
                        <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-xs leading-relaxed shadow-sm ${msg.sender === 'user' ? 'bg-brand-navy text-white rounded-br-none' : 'bg-white text-brand-slate border border-gray-100 rounded-bl-none'}`} data-id="element-80">
                          {msg.text}
                        </div>

                        {msg.articleLink && <a href={msg.articleLink.url} target="_blank" rel="noopener noreferrer" className="mt-1.5 max-w-[85%] flex items-center gap-1.5 px-3 py-2 bg-brand-navy/5 border border-brand-navy/10 rounded-lg text-[10px] hover:bg-brand-navy/10 transition-colors group" data-id="element-81">
                            <BookOpen size={10} className="text-brand-gold shrink-0" data-id="element-82" />
                            <span className="text-brand-navy font-medium group-hover:text-brand-gold transition-colors truncate" data-id="element-83">
                              📖 {msg.articleLink.title}
                            </span>
                          </a>}
                      </motion.div> : null)}
                </AnimatePresence>

                {/* Typing indicator shows briefly before each bot message */}
                {visibleMessages.length > 0 && visibleMessages.length < demoConversation.length && demoConversation[visibleMessages.length]?.sender === 'bot' && <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} className="flex items-start" data-id="element-84">
                      <div className="bg-white border border-gray-100 px-3.5 py-2.5 rounded-2xl rounded-bl-none shadow-sm" data-id="element-85">
                        <div className="flex gap-1" data-id="element-86">
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{
                      animationDelay: '0ms'
                    }} data-id="element-87" />
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{
                      animationDelay: '150ms'
                    }} data-id="element-88" />
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{
                      animationDelay: '300ms'
                    }} data-id="element-89" />
                        </div>
                      </div>
                    </motion.div>}
              </div>

              {/* Input mockup */}
              <div className="p-3 bg-white border-t border-gray-100" data-id="element-90">
                <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl" data-id="element-91">
                  <span className="text-xs text-gray-400 flex-grow" data-id="element-92">
                    Ask about focus, habits, or routines...
                  </span>
                  <div className="w-6 h-6 bg-brand-navy/10 rounded-md flex items-center justify-center" data-id="element-93">
                    <ArrowRight size={12} className="text-brand-navy" data-id="element-94" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}